import HandleQuestion from "components/_handle_question";
import { design } from "constants/form/questions/general";
import REGEX from "constants/regex";
import MainFormContext from "context/main-form";
import { useAppContext } from "context/state";
import { useGetServiceType } from "hooks/useRequest/form";
import { useCreateProject } from "hooks/useRequest/project/projectRequest";
import { useCreateUser } from "hooks/useRequest/user/userRequest";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import ReactGA from 'react-ga';
import { scrollToTop } from "utils/general";
import styles from "./steps.module.scss";

import cityService from "services/city.service";
import LastProjectService from "services/last-project";
import tokenService from "services/token.service";

import Button from "components/_button/form/submit";
import { useGetCity } from "hooks/useRequest/location/location";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import abTestService from "services/ab-test.service";
import selectedContractorService from "services/selected-contractor.service";
import trackProjectSerivce from "services/track-project.serivce";
import visitedPageService from "services/visited-page.service";
import toEnglishDigits, { persianNumbers } from "utils/persian-to-english-digit";
const ContractorLocationAvatar = dynamic(() => import("components/form/contractor-location-avatar"));



const whiteProvince: string[] = ["خراسان رضوی", "تهران", "البرز", "مازندران", "اصفهان", "فارس", "گیلان"]


interface IError {
  lastname?: string
  phoneNumber?: string
  unitNumber?: string
  region?: string
  hasDesign?: string
}

const FormUserStep = () => {
  const { values, setValues } = useContext(MainFormContext)

  const { formConfigs } = useAppContext();

  const router = useRouter()

  const [errors, setErrors] = useState<IError>({});

  const { data: citiesData, isSuccess: citiesIsSuccess } = useGetCity({
    city: values.city?._id,
    enabled: Boolean(whiteProvince.includes(values.province?.name)),
  });

  const { data: serviceTypes } = useGetServiceType()

  const { mutateAsync: createProjectMutate, isError: createProjectIsError, isSuccess: createProjectIsSuccess, error: createProjectError, isLoading: createProjectIsLoading, data: createProjectData } = useCreateProject()
  const { mutateAsync: createUserMutate, isSuccess: createUserIsSuccess, isLoading: createUserIsLoading, data: createUserData } = useCreateUser()


  // convert service name to id
  const convertServiceToId = (service: string) => {
    return serviceTypes?.data?.find(res => res.name === service)?._id
  }

  // validate input's
  // have two state for handle validation
  // one of them for all fields can validate
  // another one for save error message of each input error
  const validationError = () => {
    let error = {};
    // by default form validate is true
    // if we have one error or more
    // return false and waiting for fill data by client
    let formIsValid = true;

    // lastname
    if (typeof values?.lastname !== "undefined") {
      if (!values?.lastname.match(REGEX.TEXT_INPUT)) {
        formIsValid = false;
        error["lastname"] = "نام‌خانوادگی صحیح وارد نمایید";
      }
    }
    if (!values?.lastname) {
      formIsValid = false;
      error["lastname"] = "نام‌خانوادگی خود را وارد کنید";
    }

    // phoneNumber
    if (typeof values?.phoneNumber !== "undefined") {
      if (!values?.phoneNumber.match(REGEX.PHONE_NUMBER)) {
        formIsValid = false;
        error["phoneNumber"] = "شماره‌تلفن صحیح وارد نمایید";
      }
    }
    if (!values?.phoneNumber) {
      formIsValid = false;
      error["phoneNumber"] = "شماره‌تلفن خود را وارد کنید";
    }

    if (formConfigs.config?.includes("buildersPage")) {
      if (!values?.unitNumber) {
        formIsValid = false;
        error["unitNumber"] = "تعداد واحد خود را وارد کنید";
      }

      if (!values?.region) {
        formIsValid = false;
        error["region"] = "منطقه خود را وارد کنید";
      }
    }

    if (formConfigs.config?.includes("productPage-AB")) {
      if (!values?.hasDesign) {
        formIsValid = false;
        error["hasDesign"] = "فیلد طراحی نمیتواند خالی باشد";
      }
    }

    setErrors(error);
    return formIsValid;
  }

  const onSubmit = useCallback(() => {
    if (validationError()) {
      // scroll to top
      if (!formConfigs.config?.includes("disableScroll")) {
        scrollToTop();
      }

      if (!values.withinGoal) {
        // send google goal analytics when form submited
        ReactGA.event(formConfigs.submitEvent2 ?? {
          category: 'Submit',
          action: 'Click',
          label: 'submitForm3'
        });
      }

      if (router.pathname !== "/formps") {

        // request body
        const body = {
          lastname: values.lastname,
          phoneNumber: values.phoneNumber,
          province: values.province?._id,
          city: values.city?._id,
          forceVerify: false,
        }

        createUserMutate(body)
      }

      else { // on behsa form in "/formps" when click on submit, send user to next page
        values.nextStep()
      }
    }
  }, [values])


  useEffect(() => {
    if (createUserIsSuccess) {
      cityService.setCity(values.city)

      createProjectMutate({
        user: createUserData.data?._id,
        // if route have query string for serviceType, use that, else its was renovation form use that, else of all use cabinet
        serviceType: router.query?.service_type ?? "cabinet",
        serviceTypeId: convertServiceToId((router.query?.service_type as string) ?? "cabinet"),
        location: {
          province: values?.province?._id,
          city: values.city?._id,
          region: values?.region
        },
        sessionId: localStorage.getItem("sessionId"),
        unitNumber: values?.unitNumber,
        hasDesign: values?.hasDesign,
        unstructuredData: router.query?.unstructured_data ?? (values.persianKeyboard ? "persianKeyboard" : "Contractor"),
        selectedContractors: selectedContractorService.getContractorsId(),
        ...(abTestService.includesInstallmentUser() && {
          mightBeInterestedInInstallments: true
        }),
        submitInfo: {
          ...(router.query?.utm_source && {
            sourceOfEntry: (router.query?.utm_source as string),
          }),
          formType: router.query?.utm_campaign ?? router.query?.form_type ?? values?.formType ?? "new-form",
          sourcePage: window.location.href,
          previousPage: localStorage.getItem("prevPath"),
          landingPage: visitedPageService.getPages()?.[0]
        },
        visitedPages: visitedPageService.getPages(),
        ...(router.query?.invitedBy && {
          invitedBy: router.query.invitedBy
        }),
        ...(router.query.affId && {
          affId: router.query.affId
        }),
        trackingData: trackProjectSerivce.get()
      })
    }
  }, [createUserIsSuccess])


  // when form request was success, change current state
  useEffect(() => {
    if (createProjectIsSuccess) {

      // set project details
      LastProjectService.setProject({
        id: createProjectData.data._id,
        createdDate: createProjectData.data.createdAt,
        project: createProjectData.data
      })

      tokenService.setUser({
        ...createUserData.data,
        id: createUserData.data?._id,
        projectId: createProjectData?.data._id,
      })

      // when request for create user was success
      // if user city was not support, show a message for user
      if (!(values?.city?.serviced)) {
        setValues(prev => ({ ...prev, cityNotCovered: true }))
      }

      if (values.city?.class) return values.nextStep(1)

      else return values.nextStep(1)
    }
  }, [createProjectIsSuccess])

  // when form request have error, show a message to client
  useEffect(() => {
    if (createProjectIsError) {
      toast.error(createProjectError?.response?.data?.message)
    }
  }, [createProjectIsError])



  return (
    <div className="flex flex-column jcb flex-grow gap-30">

      <div className="flex flex-column gap-10">

        {
          (citiesIsSuccess && whiteProvince.includes(values.province?.name)) ?
            <>
              <ContractorLocationAvatar />
              <h4 className={styles.form_title}>
                بیش از «<span className="fw-800">{citiesData?.data?.countOfContractors ?? 0}</span>» کابینت ساز و نجار احراز<br /> هویت شده در شهر <span className="fw-800">{values.city?.name}</span>
              </h4>
            </>
            :
            <h4 className={styles.form_title}>
              <>اطلاعات تماس خود را وارد کنید</>
            </h4>
        }

      </div>

      <div className="flex flex-row-des m-auto-des flex-column-mb align-i-center w100 gap-25 flex-grow flex-wrap-des jcc jcc-mb">
        <div className={styles.form_item + " _validate input_block w100-mb"}>
          <label className={styles.wrapper__form_item}>
            <span className={styles.label__wrapper}>نام خانوادگی</span>
            <input
              className={`c-input ${errors?.lastname ? "input_error" : ""}`}
              name="lastname"
              onChange={(e) =>
                setValues(prev => ({ ...prev, lastname: e.target.value }))
              }
              value={values?.lastname}
              autoComplete="off"
            />
            {errors?.lastname && (
              <span className="input_alert">{errors?.lastname}</span>
            )}
          </label>
        </div>

        <div className={styles.form_item + " _validate input_block w100-mb"}>
          <label className={styles.wrapper__form_item}>
            <span className={styles.label__wrapper}>شماره تلفن</span>
            <input
              className={`c-input ${errors?.phoneNumber ? "input_error" : ""}`}
              type="tel"
              inputMode="numeric"
              name="phoneNumber"
              placeholder="09**********"
              onChange={(e) =>
                setValues(prev =>
                ({
                  ...prev,
                  phoneNumber: toEnglishDigits(e.target.value),
                  ...(persianNumbers.includes(e.target.value) && {
                    persianKeyboard: true
                  })
                })
                )
              }
              value={values?.phoneNumber}
              autoComplete="off"
            />
            {errors?.phoneNumber && (
              <span className="input_alert">{errors?.phoneNumber}</span>
            )}
          </label>
        </div>

        {formConfigs.config?.includes("buildersPage") && (
          <>
            <div className="_validate input_block">
              <input
                className={`c-input ${errors?.unitNumber ? "input_error" : ""}`}
                type="number"
                inputMode="numeric"
                name="unitNumber"
                placeholder="تعداد واحد"
                onChange={(e) =>
                  setValues(prev => ({ ...prev, unitNumber: e.target.value }))
                }
                value={values?.unitNumber}
              />
              {errors?.unitNumber && (
                <span className="input_alert">{errors?.unitNumber}</span>
              )}
            </div>

            <div className="_validate input_block">
              <textarea
                className={`c-input ${errors?.region ? "input_error" : ""}`}
                name="region"
                placeholder="منطقه"
                rows={4}
                onChange={(e) =>
                  setValues(prev => ({ ...prev, region: e.target.value }))
                }
                value={values?.region}
              />
              {errors?.region && (
                <span className="input_alert">{errors?.region}</span>
              )}
            </div>
          </>
        )}
        {formConfigs.config?.includes("productPage-AB") && (
          <div className="_validate">
            <HandleQuestion
              constant={design}
              setSelected={setValues}
              selected={values}
            />
            {errors?.hasDesign && (
              <span className="input_alert">{errors?.hasDesign}</span>
            )}
          </div>
        )}

        <p className={styles.rules}>وارد کردن شماره‌تلفن به معنی پذیرش <Link href="/rules" className={styles.rulesLink} legacyBehavior>شرایط و قوانین</Link> می‌باشد</p>

      </div>

      <div className='flex flex-row jcb gap-15 w100'>
        <Button
          id="submit_form"
          className="flex-grow"
          loading={createUserIsLoading || createProjectIsLoading}
          onClick={() => onSubmit()}
          animation
        >
          مرحله بعد
        </Button>

        <button
          disabled={(createUserIsLoading || createProjectIsLoading)}
          onClick={() => values.prevStep()}
          className="sidelong__btn fw-700"
        >
          مرحله قبل
        </button>
      </div>
    </div>
  );
};

export default memo(FormUserStep);
